import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { IconArrowLeft, IconPlus } from "@tabler/icons-react";
import {
    addShooting,
    getAthleteById,
    getShootings,
    getEventById,
} from "../db";
import LoadinPage from "../Components/LoadingPage";
import ShootingPreview5 from "../Components/Shooting/ShootingPreview5";

const Shooting = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [shootings, setShootings] = useState([]);
    const [groupedShootings, setGroupedShootings] = useState({});
    const [athletes, setAthletes] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchShootingsAndAthletes = async () => {
            try {
                const foundShootings = await getShootings();
                setShootings(foundShootings);

                // Load missing athletes
                const missingAthleteIds = [...new Set(
                    foundShootings
                        .filter(s => s.athlete && !athletes[s.athlete])
                        .map(s => s.athlete)
                )];

                const athleteData = await Promise.all(
                    missingAthleteIds.map(async (id) => {
                        const athlete = await getAthleteById(id);
                        return athlete ? { [id]: athlete } : null;
                    })
                );

                setAthletes(prev => ({
                    ...prev,
                    ...Object.assign({}, ...athleteData.filter(Boolean)),
                }));

                // Group by date & event
                const groups = await groupShootingsByDate(foundShootings);
                setGroupedShootings(groups);
            } catch (error) {
                console.error("Error loading shootings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchShootingsAndAthletes();

        const handleEventsUpdated = async (e) => {
            if (e.tables?.includes('shooting')) {
                fetchShootingsAndAthletes();
            }
        }

        window.addEventListener('serverSync', handleEventsUpdated);

        return () => {
            window.removeEventListener('serverSync', handleEventsUpdated);
        };
    }, [navigate, t]);

    const handleNewShooting = async () => {
        try {
            const shootingId = await addShooting({});
            navigate(`/shooting/${shootingId}`);
        } catch (error) {
            console.error("Error adding shooting:", error);
        }
    };

    const groupShootingsByDate = async (shootings) => {
        const eventsMap = {};
        const grouped = {};

        const sorted = shootings.sort((a, b) => { const aDate = a.date || 0; const bDate = b.date || 0; return bDate - aDate });

        for (const shooting of sorted) {
            let dateKey = '';

            if (shooting.date) {
                const date = new Date(shooting.date * 1000);
                if (date) {
                    dateKey = new Intl.DateTimeFormat("cs-CZ", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                    }).format(date);
                }
            }
            let groupKey = dateKey;

            if (shooting.event) {
                if (!eventsMap[shooting.event]) {
                    eventsMap[shooting.event] = await getEventById(shooting.event);
                }
                const eventTitle = eventsMap[shooting.event]?.title || t("event.unknown");
                groupKey = `${dateKey} - ${eventTitle}`;
            }

            if (!grouped[groupKey]) {
                grouped[groupKey] = [];
            }

            grouped[groupKey].push(shooting);
        }

        return grouped;
    };

    if (loading) {
        return (
            <div className="p-4 flex flex-col h-full relative">
                <div className="flex items-center pb-4 w-full">
                    <div className="shrink-0 w-10">
                        <Link to={`/athletes`}>
                            <IconArrowLeft className="w-6 h-auto" />
                            <span className="sr-only">{t("athletesDetail.backToDetail")}</span>
                        </Link>
                    </div>
                    <div className="grow text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0"></div>
                    <div className="shrink-0 w-10"></div>
                </div>
                <div className="grow overflow-y-auto overflow-x-hidden">
                    <LoadinPage />
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 flex flex-col h-full relative">
            <h1 className="text-2xl uppercase text-center font-black transform -skew-x-12 shrink-0 pb-4">
                {t("shootingrange.headline")}
            </h1>

            <div className="grow overflow-y-auto overflow-x-hidden">
                {Object.keys(groupedShootings).length === 0 ? (
                    <p>{t("athletesShooting.noShootings")}</p>
                ) : (
                    <div className="flex flex-col gap-6 px-2">
                        {Object.entries(groupedShootings).map(([groupKey, groupShootings]) => (
                            <div key={groupKey} className="flex flex-col gap-2 text-xs">
                                <h2 className="text-lg font-bold">{groupKey}</h2>
                                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 px-2">
                                    {groupShootings.map((shooting) => (
                                        <Link
                                            key={shooting.id}
                                            to={`/shooting/${shooting.id}`}
                                            className={(shooting.type === 'race' ? 'button-primary' : 'button-secondary') + " flex-col !items-start justify-between"}
                                        >
                                            <div>
                                                {shooting.athlete && athletes[shooting.athlete] && (
                                                    <div>
                                                        {athletes[shooting.athlete].nickname ||
                                                        `${athletes[shooting.athlete].firstname} ${athletes[shooting.athlete].lastname}`}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-2 items-center">
                                                {shooting.points?.length === 5 && (
                                                    <ShootingPreview5 shooting={shooting} />
                                                )}
                                                <div className="aspect-square h-4 flex items-center justify-center bg-[var(--dark-color)] border border-[var(--dark-color)] text-[var(--light-color)] text-[0.5rem] leading-none">
                                                    {t("shooting." + shooting.position)}
                                                </div>
                                                <div>
                                                    {shooting.date && (() => {
                                                        const date = new Date(shooting.date * 1000);
                                                        const hours = String(date.getHours()).padStart(2, "0");
                                                        const minutes = String(date.getMinutes()).padStart(2, "0");
                                                        return `${hours}:${minutes}`;
                                                    })()}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button
                onClick={handleNewShooting}
                className="p-4 rounded-full bg-[var(--dark-color)] text-[var(--light-color)] absolute right-2 bottom-2"
            >
                <IconPlus className="w-6 h-auto" />
                <span className="sr-only">{t("athletesShooting.addNew")}</span>
            </button>
        </div>
    );
};

export default Shooting;
